/* eslint-disable no-unused-vars */
import { useState, useEffect, useMemo } from 'react';
import { mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import { useQuery } from '@tanstack/react-query';
import { CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchBonusDetails } from '../../../store/apis/bonusService';
import { toFixedTwoDecimal } from '../../../utils/number.util';
import { getAuthUser } from '../../../utils/auth.util';
import * as icons from '../../../assets';
import { fetchHRCodeList } from '../../../store/apis/commonService';

import './QuarterlyBonusWidget.scss';

const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

function QuarterlyBonusWidget() {
  const navigate = useNavigate();
  const actualCurrentYear = new Date().getFullYear();
  const user = getAuthUser();
  const [currentYear, setCurrentYear] = useState(null);
  const [currentYearDescription, setCurrentYearDescription] = useState(null);
  // Use state for eligibilityByQuarter
  const [eligibilityByQuarter, setEligibilityByQuarter] = useState([
    0, 0, 0, 0,
  ]);
  const [actualByQuarter, setActualByQuarter] = useState([0, 0, 0, 0]);

  const { data: currentFiscalYear } = useQuery({
    queryKey: ['currentFiscalYear'],
    queryFn: () => fetchHRCodeList({ type: 'FISCAL_YEAR' }),
    enabled: true,
    keepPreviousData: true,
  });

  const getDefaultActiveFiscalYear = () => {
    const foundCurrentYear = currentFiscalYear?.find(
      year => year?.IsActive === '1',
    );

    return foundCurrentYear
      ? parseInt(foundCurrentYear?.Code, 10)
      : actualCurrentYear;
  };

  const getDefaultActiveFiscalYearDescription = () => {
    const foundCurrentYear = currentFiscalYear?.find(
      year => year?.IsActive === '1',
    );

    return foundCurrentYear ? foundCurrentYear?.Description : actualCurrentYear;
  };

  useEffect(() => {
    if (currentFiscalYear) {
      setCurrentYear(getDefaultActiveFiscalYear);
      setCurrentYearDescription(getDefaultActiveFiscalYearDescription);
    }
  }, [currentFiscalYear]);

  const {
    data: fetchedSummary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useQuery({
    queryKey: ['fetchedSummary', currentYear],
    queryFn: () =>
      fetchBonusDetails({
        type: 'SUMMARY',
        yr: currentYear,
        empCode: user.employeeId || '',
      }),
    enabled: !!currentYear,
  });

  useEffect(() => {
    if (Array.isArray(fetchedSummary)) {
      const eligibility = quarters.map(qtr =>
        fetchedSummary
          .filter(item => `Q${item?.Qtr}` === qtr)
          .reduce(
            (sum, item) => sum + (parseFloat(item?.['Total Eligibility']) || 0),
            0,
          ),
      );
      setEligibilityByQuarter(eligibility);

      const actual = quarters.map(qtr =>
        fetchedSummary
          .filter(item => `Q${item?.Qtr}` === qtr)
          .reduce((sum, item) => sum + (parseFloat(item?.TotalActual) || 0), 0),
      );
      setActualByQuarter(actual);
    }
  }, [fetchedSummary]);

  const chartSeries = useMemo(
    () => [
      { name: 'Target', data: eligibilityByQuarter },
      { name: 'Achieved', data: actualByQuarter },
    ],
    [eligibilityByQuarter, actualByQuarter],
  );

  const chartOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: {
        enabled: false,
        animateGradually: { enabled: false },
        dynamicAnimation: { enabled: false },
      },
    },
    grid: {
      show: true,
      borderColor: '#000',
      strokeDashArray: 10,
      position: 'back',
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: -10,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadiusApplication: 'around',
      },
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ['#FFC0CB'] },
    xaxis: {
      categories: quarters,
      labels: {
        style: {
          colors: new Array(quarters.length).fill('#000'),
          fontWeight: 'bold',
        },
      },
    },
    yaxis: {
      min: 0,
      max: 1.2,
      forceNiceScale: false,
      labels: {
        formatter: val => `${val * 100}%`,

        style: {
          fontWeight: 'bold',
        },
        offsetX: -10,
      },
    },
    fill: { opacity: 1 },
    axisBorder: {
      show: true,
      height: 2,
    },
    colors: ['#088ab2', '#079455'],
    legend: {
      position: 'bottom',
      markers: {
        shape: 'circle',
      },
    },

    tooltip: {
      theme: 'dark',
      y: {
        formatter: val => `${toFixedTwoDecimal(val * 100)}%`,
      },
      followCursor: true,
    },
  };

  return (
    <Card className="quarterly-widget">
      <CardHeader
        className="quarterly-widget__header d-flex flex-column pb-0"
        onClick={() => navigate('/bonus')}
      >
        <div className="gap-2 pb-0 quarterly-widget__header-row">
          <div className="d-flex flex-row align-items-center gap-2">
            <img
              src={icons.iconsDashboardBonus}
              alt="Bonus Icon"
              height="20px"
            />
            <span className="quarterly-widget__header-title fs-5">
              {`Quarterly Bonus - ${currentYearDescription}`}
            </span>
          </div>
          <button type="button" className="btn btn-link non-btn">
            <Icon path={mdiChevronRight} size={1} color="#017EBD" />
          </button>
        </div>
        <div
          style={{
            borderBottom: '1px solid #d0d5dd',
            height: '1px',
            width: '98%',
            paddingTop: '10px',
          }}
        />
      </CardHeader>
      <CardBody className="d-flex justify-content-center align-items-center pt-2 w-100">
        {!isSummaryLoading && !isSummaryError && (
          <div className="chart-box">
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type="bar"
              height="100%"
              width="100%"
            />
          </div>
        )}
        {isSummaryLoading && (
          <div className="text-center py-5">
            <CircularProgress />
          </div>
        )}
        {isSummaryError && (
          <div className="text-center py-5 text-danger">
            Error loading summary data.
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default QuarterlyBonusWidget;
